import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Manages the user's saved talks ("My playlist").
 *
 *   - items: list of bookmarked sessions (each is a session_to_public dict +
 *     bookmarked_at)
 *   - has(sessionId): O(1) lookup for the schedule rows + talk modal
 *   - toggle(sessionId): optimistic add/remove
 */
export default function useBookmarks() {
  const [items, setItems] = useState([]); // [{session_id, title, ..., bookmarked_at}]
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/me/bookmarks");
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const has = useCallback(
    (sessionId) => items.some((it) => it.session_id === sessionId),
    [items]
  );

  const toggle = useCallback(
    async (session, sessionMeta) => {
      // `session` may be a string id or a session-like object. `sessionMeta`
      // is optional and used to populate the row optimistically.
      const sid = typeof session === "string" ? session : session?.session_id;
      if (!sid) return;
      const currently = items.some((it) => it.session_id === sid);
      setBusyId(sid);
      // Optimistic
      setItems((prev) => {
        if (currently) return prev.filter((it) => it.session_id !== sid);
        const stub = sessionMeta || (typeof session === "object" ? session : { session_id: sid });
        return [
          { ...stub, session_id: sid, bookmarked_at: new Date().toISOString() },
          ...prev,
        ];
      });
      try {
        if (currently) {
          await api.delete(`/me/bookmarks/${encodeURIComponent(sid)}`);
        } else {
          await api.post(`/me/bookmarks/${encodeURIComponent(sid)}`);
          // Reconcile with server's full detail so we get description, speaker, etc.
          refresh();
        }
      } catch {
        // Revert on failure
        refresh();
      } finally {
        setBusyId(null);
      }
    },
    [items, refresh]
  );

  return { items, loaded, has, toggle, busyId, refresh };
}
